import React, { useState, useEffect } from "react";
import { IoClose } from "react-icons/io5";
import { colors } from "../../../../constants/colors";
import { showErrorToast } from "../../../../utils/toast";
import { appApiHandler } from "../../../../api/appApiHandler";
import "./offersModal.css";
import { useSelector } from "react-redux";
import { getErrorMessage } from "../../../../constants/globalFunction";

const OffersModal = ({
  visible,
  onClose,
  coupons,
  subTotal,
  selectedCoupon,
  onApplyCoupon,
  onRemoveCoupon,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCoupons, setFilteredCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applyingCouponId, setApplyingCouponId] = useState(null);
  const { userData } = useSelector((state) => state.userState);

  useEffect(() => {
    if (searchTerm) {
      const filtered = coupons?.filter(coupon =>
        coupon?.code?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCoupons(filtered);
    } else {
      setFilteredCoupons(coupons);
    }
  }, [searchTerm, coupons]);

  const getDiscountText = (coupon) => {
    if (coupon.discount_type === "flat") {
      return `Flat ₹${coupon.discount_amount} off`;
    } else {
      return `${coupon.discount_percentage}% off`;
    }
  };

  const handleApplyCoupon = async (coupon) => {
    try {
      if (coupon?.min_order_amount && parseFloat(subTotal) < parseFloat(coupon?.min_order_amount)) {
        const diffAmount = (coupon.min_order_amount - subTotal).toFixed(2);
        showErrorToast(`Add items worth ₹${diffAmount} more to apply this coupon`);
        return;
      }

      setLoading(true);
      setApplyingCouponId(coupon?._id);
      const response = await appApiHandler.checkValidityOfCoupon({
        user_id: userData?._id,
        coupon_id: coupon?._id
      });

      if (response?.status === 200) {
        onApplyCoupon?.(coupon);
      } else {
        showErrorToast(getErrorMessage(response?.message));
      }
    } catch (error) {
      showErrorToast(getErrorMessage(error));
    } finally {
      setLoading(false);
      setApplyingCouponId(null);
    }
  };

  const handleClose = () => {
    setSearchTerm("");
    setFilteredCoupons(coupons);
    setLoading(false);
    setApplyingCouponId(null);
    onClose()
  }

  useEffect(() => {
    if (visible) {
      setSearchTerm("");
      setFilteredCoupons(coupons);
      setLoading(false);
      setApplyingCouponId(null);
    }
  }, [visible])

  if (!visible) return null;

  return (
    <div className="offers-modal-overlay">
      <div className="offers-modal">
        <div className="offers-modal-header">
          <h2>Available Offers</h2>
          <button className="offers-modal-close" onClick={handleClose}>
            <IoClose size={23} color={colors.primary} />
          </button>
        </div>

        <div className="offers-search">
          <input
            type="text"
            placeholder="Search code"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="offers-search-input"
          />
        </div>

        <div className="offers-list">
          {filteredCoupons.length > 0 ? (
            filteredCoupons.map((coupon) => {
              const isSelected = selectedCoupon?._id === coupon?._id;

              return (
                <div key={coupon?._id} className="offer-card">
                  <div className="offer-header">
                    <div className="offer-code">{coupon?.code}</div>
                    <div className="offer-discount">
                      {getDiscountText(coupon)}
                    </div>
                  </div>
                  {coupon?.description && (
                    <div className="offer-description">{coupon.description}</div>
                  )}
                  {coupon?.expiry_date && (
                    <div className="offer-validity">
                      Valid until&nbsp;{coupon?.expiry_date}
                    </div>
                  )}
                  <button
                    className={`apply-button${isSelected ? " apply-button-remove" : ""}`}
                    disabled={loading}
                    onClick={() => {
                      if (isSelected) {
                        onRemoveCoupon?.();
                      } else {
                        handleApplyCoupon(coupon);
                      }
                    }}
                  >
                    {isSelected ? "Remove" : applyingCouponId === coupon?._id ? "Applying..." : "Apply"}
                  </button>
                </div>
              );
            })
          ) : (
            <div className="no-offers">
              {searchTerm ? "No offers found matching your search." : "No offers available."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OffersModal;
